import { AssetIcon } from 'assets/icons/AssetIcon';
import { AssetImage } from 'assets/images/AssetImage';
import useConditionalTimeout from 'beautiful-react-hooks/useConditionalTimeout';
import useInterval from 'beautiful-react-hooks/useInterval';
import cx from 'classnames';
import PageActions from 'components/page-actions/PageActions';
import TimerText from 'components/timer-text/TimerText';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { I18nNamespace } from 'constants/i18n.const';
import {
  CURRENCY_SYMBOL,
  SECOND_VAL,
  TIME_REFRESHABLE_DURATION,
} from 'constants/photo.const';
import { useAppContext } from 'context/AppContext';
import { useSoundContext } from 'context/SoundContext';
import { formatMoneyVN, mToSec } from 'helpers/math.helper';
import { diffFromDate, secondToTimeFormat } from 'helpers/time.helper';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { usePageTimer } from 'hooks/usePageTimer';
import { useTranslation } from 'hooks/useTranslation';
import moment from 'moment';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/store-hooks';
import { useCheckPayment } from '../payment/useCheckPayment';
import './qr-code.css';

function QRCode() {
  const { T } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const navigate = useNavigate();
  const { getPrevPath } = useNewPhotoLifeCycleStep();
  const { audio } = useSoundContext();
  const { isOnline } = useAppContext();
  const { second } = usePageTimer();
  const {
    costOfPayment,
    isEnoughDeposit,
    handleCheckBeforeCreateQRPayment,
    handleCreateMomoQRCode,
    handleCreateVNPayQRCode,
  } = useCheckPayment();
  const {
    momoTransactionData,
    momoTransactionExpireDate,
    depositAmount,
    isMomoTransactionLoading,
  } = useAppSelector((state) => state.selfeApp);
  const {
    isVerticalScreen,
    isTurnOnVNPayMethodPayment,
    isTurnOnMomoMethodPayment,
  } = useAppSelector((s) => s.booth);

  const [currentTime, setCurrentTime] = useState<Date | null>(
    moment().toDate(),
  );
  const [timeRefreshable, setTimeRefreshable] = useState<number>(
    mToSec(TIME_REFRESHABLE_DURATION),
  );

  // click Back
  const handleBack = () => {
    audio?.playBackClick?.();
    navigate(getPrevPath() || '');
  };

  // thời gian chuyển khoản còn lại
  const [, clearInterval] = useInterval(async () => {
    setCurrentTime(moment().toDate());
    setTimeRefreshable((s) => Math.max(s - 1, 0));
  }, SECOND_VAL);

  const handleRefreshQRCode = () => {
    setTimeRefreshable(mToSec(TIME_REFRESHABLE_DURATION));
    if (isTurnOnVNPayMethodPayment) {
      handleCreateVNPayQRCode();
    } else if (isTurnOnMomoMethodPayment) {
      handleCreateMomoQRCode();
    }
  };

  const renderPaymentActions = () => (
    <PageActions
      NextButtonProps={{
        children: T('common:continue'),
        disabled: !isEnoughDeposit || !isOnline,
      }}
      handleBack={handleBack}
      className={cx({ 'qr-code__page-actions--vertical': isVerticalScreen })}
      hideNext
    />
  );

  useConditionalTimeout(
    () => {
      handleCheckBeforeCreateQRPayment();
    },
    SECOND_VAL * 2,
    costOfPayment <= depositAmount,
  );

  React.useEffect(
    () => () => {
      clearInterval();
    },
    [clearInterval],
  );

  return (
    <>
      <div className="qr-code">
        <TimerText second={second} />
        {isVerticalScreen && renderPaymentActions()}
        <div className="flex items-center justify-center qr-code__wrapper">
          <div className="qr-code__momo-wrapper">
            <div className="qr-code__momo-payment-info">
              {(() => {
                if (isTurnOnVNPayMethodPayment) {
                  return (
                    <img
                      src={AssetImage.VNPayLogo}
                      alt="vnpay icon"
                      style={{ width: '9rem', height: '7rem' }}
                    />
                  );
                }

                if (isTurnOnMomoMethodPayment) {
                  return <img src={AssetImage.MomoLogo} alt="momo icon" />;
                }
              })()}
              <div className="qr-code__momo-payment-money-info">
                {costOfPayment <= depositAmount ? (
                  <>
                    <Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
                      {T(`${I18nNamespace.COMMON}:successBanking`)}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
                      {secondToTimeFormat(
                        diffFromDate(currentTime, momoTransactionExpireDate) ||
                          0,
                      )}
                    </Typography>
                    <div className="divide-vertical-line" />
                    <Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
                      <strong>
                        {formatMoneyVN(Math.max(costOfPayment - depositAmount))}{' '}
                        {CURRENCY_SYMBOL}
                      </strong>
                    </Typography>
                  </>
                )}
              </div>
            </div>
            <QRCodeCanvas
              style={{ height: 'auto', maxWidth: '100%', width: '45rem' }}
              value={
                momoTransactionData?.Data ||
                momoTransactionData?.qrCodeUrl ||
                ''
              }
            />
            {costOfPayment > depositAmount && (
              <div
                className={cx('qr-code__qr-refresh-wrapper', {
                  'qr-code__qr-refresh-wrapper--disabled': timeRefreshable > 0,
                  'qr-code__qr-refresh-wrapper--loading':
                    isMomoTransactionLoading,
                })}
              >
                <button
                  type="button"
                  className="qr-code__btn-qr-refresh"
                  onClick={handleRefreshQRCode}
                  disabled={timeRefreshable > 0}
                >
                  <Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
                    {timeRefreshable
                      ? secondToTimeFormat(timeRefreshable)
                      : T(`${I18nNamespace.COMMON}:qrRefresh`)}
                  </Typography>
                  <AssetIcon.RefreshIcon />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isVerticalScreen && renderPaymentActions()}
    </>
  );
}

export default QRCode;
